// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@tps/App/Store/Reducers";
import IconHeaderNavDropdownDown from "@tps/XDL/Icons/IconHeaderNavDropdownDown";
import { mergeWithCss, xClassNames, xScreen } from "@tps/XDL/Utilities";
import React, { FC, MouseEventHandler } from "react";
import { useState } from "react";

interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TypographyProps;
    title?: string;
    subTitle?: string;
    isOpen?: boolean;
    showDropDownIcon?: boolean;
    replaceIcon?: boolean;
    extendedStyles?: HeaderClickableStyle;
}

type Props = HeaderClickableProps & ClickableProps;

export interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    arrow?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
}

export const headerClickableStyles: HeaderClickableStyle = {};

const HeaderClickable: FC<Props> = ({
    iconProps,
    titleProps,
    title,
    subTitle,
    extendedStyles,
    showDropDownIcon = true,
    isOpen,
    replaceIcon,
    children,
    ...otherProps
}) => {
    const { isTabletScreen, isMobileScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    }));
    const classNames = xClassNames.append([isTabletScreen && "mobile-mode", isOpen && "opened"], otherProps.className);
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));

    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames}>
            {replaceIcon
                ? children
                : iconProps && <Icon {...styles.icon} size={isTabletScreen ? 18 : 16} {...iconProps} />}

            {!isTabletScreen && (
                <>
                    {title && (
                        <Typography className="headerClickable-title" {...styles.title} {...titleProps}>
                            {title}
                        </Typography>
                    )}
                    {showDropDownIcon && (
                        <Icon className="arrow" src={IconHeaderNavDropdownDown} {...styles.arrow} size={13} />
                    )}
                </>
            )}
        </Clickable>
    );
};

export default HeaderClickable;
